import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

type ErrorBoundaryProps = {
  children: ReactNode
  /** Short name of the area, used in the message ("the school wizard"). */
  area?: string
  /** Called when the admin presses "Try again", before the content is shown again. */
  onReset?: () => void
  /** When this value changes (for example the route), a shown error is cleared automatically. */
  resetKey?: unknown
}

type ErrorBoundaryState = { hasError: boolean }

/** Keeps a render error inside one area of the page instead of emptying the whole app. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Render error caught by ErrorBoundary', error, info.componentStack)
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.hasError && !Object.is(prev.resetKey, this.props.resetKey)) {
      this.setState({ hasError: false })
    }
  }

  private handleReset = () => {
    this.props.onReset?.()
    this.setState({ hasError: false })
  }

  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <Alert variant='destructive' role='alert'>
        <AlertCircle className='h-4 w-4' />
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className='space-y-3'>
          <p>We hit a problem showing {this.props.area ?? 'this page'}. Your other work is safe.</p>
          <Button type='button' variant='outline' size='sm' onClick={this.handleReset}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    )
  }
}

/** Error boundary for a layout's outlet: clears itself when the route changes. */
export function RouteErrorBoundary({ children, area }: { children: ReactNode; area?: string }) {
  const { pathname } = useLocation()
  return (
    <ErrorBoundary area={area} resetKey={pathname}>
      {children}
    </ErrorBoundary>
  )
}
